import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  collection,
  getDocs,
  getDocsFromServer,
  query,
  where,
} from 'firebase/firestore';
import { db } from '../firebase';
import './HighlightsWidget.css';
import useAuthStore from '../stores/authStore';
import {
  buildHighlightEntry,
  formatCountWithSpecies,
  formatPercent,
  formatTime,
} from '../utils/highlights';
import { buildLocationSet, normalizeLocationId } from '../utils/location';
import { resolveAccessLocationId } from '../utils/access';
import { trackButton, trackEvent } from '../utils/analytics';
import { downloadEntryMedia, isLikelyVideoUrl } from '../utils/media';
import { FiDownload, FiEdit2, FiStar } from 'react-icons/fi';
import { FaFacebookF } from 'react-icons/fa';
import FacebookVideoPublishModal from './FacebookVideoPublishModal';
import {
  getFacebookConnectionErrorMessage,
  loadFacebookPageConnection,
} from '../utils/FB/facebookPageConnection';
import {
  applySightingCorrection,
  buildCorrectionNote,
  describeSpeciesChange,
} from '../utils/sightings/corrections';
import {
  applySightingHighlight,
  getHighlightStateKey,
} from '../utils/sightings/highlights';
import {
  DEFAULT_WHATSAPP_CLIP_SELECTION,
  formatWhatsAppClipSelection,
  promptForWhatsAppClipSelection,
} from '../utils/whatsappClip';
import { reviewAlertSpeciesSpelling } from '../utils/alertSpellCheck';
const HIGHLIGHTS_PAGE_SIZE = 50;
const SEND_WHATSAPP_ENDPOINT =
  process.env.REACT_APP_SEND_WHATSAPP_ENDPOINT ||
  'https://send-manual-whatsapp-alert-186628423921.us-central1.run.app';
const DELETE_SIGHTING_ENDPOINT =
  process.env.REACT_APP_DELETE_SIGHTING_ENDPOINT ||
  'https://delete-sighting-media-186628423921.us-central1.run.app';

const pickFirstSource = (...sources) =>
  sources.find((src) => typeof src === 'string' && src.length > 0) || null;
const toAnalyticsError = (value) => String(value || '').slice(0, 120);
const getHighlightLoadErrorMessage = (error, fallback) => {
  const message = String(error?.message || '').toLowerCase();
  if (message.includes('index') && message.includes('highlightedat')) {
    return 'Highlights query needs a Firestore index before it can load.';
  }
  return fallback;
};

const getSortTime = (value) => (
  value instanceof Date && !Number.isNaN(value.getTime()) ? value.getTime() : null
);

const getHighlightSortTime = (entry) => getSortTime(entry?.highlightedAt || null);

const getFallbackSortTime = (entry) => getSortTime(entry?.spottedAt || entry?.createdAt || null);

const compareHighlightsByHighlightedAt = (left, right) => {
  const leftHighlightedAt = getHighlightSortTime(left);
  const rightHighlightedAt = getHighlightSortTime(right);

  if (leftHighlightedAt !== rightHighlightedAt) {
    if (rightHighlightedAt === null) return -1;
    if (leftHighlightedAt === null) return 1;
    return rightHighlightedAt - leftHighlightedAt;
  }

  const leftFallbackTime = getFallbackSortTime(left) ?? 0;
  const rightFallbackTime = getFallbackSortTime(right) ?? 0;
  return rightFallbackTime - leftFallbackTime;
};

const formatDateTimeLabel = (value) => {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) {
    return '';
  }

  return `${value.toLocaleDateString()} ${formatTime(value)}`.trim();
};

const pickHighlightSpeciesDoc = (parentDoc, speciesDocs) => {
  if (!Array.isArray(speciesDocs) || speciesDocs.length === 0) {
    return null;
  }

  const highlightedDocId =
    typeof parentDoc?.highlightSourceSpeciesDocId === 'string'
      ? parentDoc.highlightSourceSpeciesDocId.trim()
      : '';
  if (highlightedDocId) {
    const matchingDoc = speciesDocs.find((speciesDoc) => speciesDoc?.id === highlightedDocId);
    if (matchingDoc) {
      return matchingDoc;
    }
  }

  const highlightedSpeciesDoc = speciesDocs.find((speciesDoc) => speciesDoc?.isHighlighted);
  if (highlightedSpeciesDoc) {
    return highlightedSpeciesDoc;
  }

  const primarySpecies = typeof parentDoc?.primarySpecies === 'string' ? parentDoc.primarySpecies.trim() : '';
  if (primarySpecies) {
    const primaryDoc = speciesDocs.find((speciesDoc) => speciesDoc?.id === primarySpecies);
    if (primaryDoc) {
      return primaryDoc;
    }
  }

  return speciesDocs[0];
};

export default function HighlightsWidget() {
  const [allHighlights, setAllHighlights] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeEntry, setActiveEntry] = useState(null);
  const [modalViewMode, setModalViewMode] = useState('standard');
  const [isHdEnabled, setIsHdEnabled] = useState(false);
  const [sendStatusMap, setSendStatusMap] = useState({});
  const sendingEntryIdsRef = useRef(new Set());
  const [facebookConnection, setFacebookConnection] = useState({
    loading: true,
    connected: false,
    selectedPageId: '',
    pages: [],
    error: '',
  });
  const [facebookPublishTarget, setFacebookPublishTarget] = useState(null);
  const [deleteStatusMap, setDeleteStatusMap] = useState({});
  const [downloadStatusMap, setDownloadStatusMap] = useState({});
  const [highlightStatusMap, setHighlightStatusMap] = useState({});
  const [editTarget, setEditTarget] = useState(null);
  const [editMode, setEditMode] = useState('animal');
  const [editSpeciesInput, setEditSpeciesInput] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');
  const [refreshToken, setRefreshToken] = useState(0);
  const preservePageOnNextLoadRef = useRef(false);
  const isMountedRef = useRef(true);
  const [currentPage, setCurrentPage] = useState(0);
  const role = useAuthStore((state) => state.role);
  const locationIds = useAuthStore((state) => state.locationIds);
  const isAccessLoading = useAuthStore((state) => state.isAccessLoading);
  const accessError = useAuthStore((state) => state.accessError);
  const user = useAuthStore((state) => state.user);
  const editSpeciesInputRef = useRef(null);

  const allowedLocationSet = useMemo(() => buildLocationSet(locationIds), [locationIds]);
  const isAdmin = role === 'admin';
  const accessReady = !isAccessLoading;
  const noAssignedLocations = accessReady && !isAdmin && allowedLocationSet.size === 0;

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!editTarget || editMode !== 'animal' || editSaving) {
      return;
    }

    const input = editSpeciesInputRef.current;
    if (input) {
      input.focus();
      input.select();
    }
  }, [editMode, editSaving, editTarget]);

  useEffect(() => {
    setIsHdEnabled(false);
  }, [activeEntry]);

  useEffect(() => {
    const emptyConnection = {
      loading: false,
      connected: false,
      selectedPageId: '',
      pages: [],
      error: '',
    };
    setFacebookPublishTarget(null);
    setFacebookConnection({ ...emptyConnection, loading: Boolean(user) });
    if (!user) return undefined;

    let didCancel = false;
    loadFacebookPageConnection()
      .then((connection) => {
        if (didCancel) return;
        setFacebookConnection({
          ...emptyConnection,
          connected: Boolean(connection?.connected),
          selectedPageId: connection?.selectedPageId || '',
          pages: Array.isArray(connection?.pages) ? connection.pages : [],
        });
      })
      .catch((connectionError) => {
        if (didCancel) return;
        setFacebookConnection({
          ...emptyConnection,
          error: getFacebookConnectionErrorMessage(connectionError),
        });
      });

    return () => {
      didCancel = true;
    };
  }, [user]);

  const actorName = useMemo(() => {
    if (!user) {
      return 'Admin';
    }

    const candidates = [user.displayName, user.email, user.phoneNumber];
    const preferred = candidates.find((value) => typeof value === 'string' && value.trim().length > 0);

    if (preferred) {
      return preferred.trim();
    }

    return typeof user.uid === 'string' && user.uid.length > 0 ? user.uid : 'Admin';
  }, [user]);

  const refreshHighlights = useCallback(({ preservePage = false } = {}) => {
    preservePageOnNextLoadRef.current = preservePage;
    if (!preservePage) {
      setCurrentPage(0);
    }
    setRefreshToken((prev) => prev + 1);
  }, []);

  const loadHighlights = useCallback(async () => {
    if (!accessReady) {
      return;
    }

    if (!isAdmin && allowedLocationSet.size === 0) {
      setAllHighlights([]);
      setError('');
      setCurrentPage(0);
      setLoading(false);
      return;
    }

    const shouldPreservePage = preservePageOnNextLoadRef.current;
    preservePageOnNextLoadRef.current = false;
    setLoading(true);
    setError('');
    try {
      const highlightsQuery = query(
        collection(db, 'sightings'),
        where('isHighlighted', '==', true),
      );
      let parentSnapshot;
      try {
        parentSnapshot = await getDocsFromServer(highlightsQuery);
      } catch (serverError) {
        parentSnapshot = await getDocs(highlightsQuery);
      }
      if (!isMountedRef.current) {
        return;
      }

      const parentDocs = parentSnapshot.docs
        .map((docSnap) => ({
          id: docSnap.id,
          ref: docSnap.ref,
          data: docSnap.data() || {},
        }))
        .filter(({ data }) => !data?.deletedAt)
        .filter(({ data }) => {
          if (isAdmin) {
            return true;
          }

          const normalizedLocation = normalizeLocationId(
            resolveAccessLocationId(
              data?.cameraId,
              data?.clientId,
              data?.locationId,
              data?.location,
            ),
          );
          return allowedLocationSet.has(normalizedLocation);
        });

      const nextHighlights = await Promise.all(
        parentDocs.map(async ({ id, ref, data }) => {
          const speciesSnapshot = await getDocs(collection(db, 'sightings', id, 'perSpecies'));
          if (!isMountedRef.current) {
            return null;
          }

          const speciesDocs = speciesSnapshot.docs
            .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
            .filter((speciesDoc) => !speciesDoc?.deletedAt);

          const selectedSpeciesDoc = pickHighlightSpeciesDoc(data, speciesDocs);
          const parentDoc = { id, ...data };
          const speciesDoc = selectedSpeciesDoc || parentDoc;

          return {
            ...buildHighlightEntry({
              category: 'manualHighlight',
              speciesDoc,
              parentDoc,
              extra: {
                label: 'Starred Highlight',
                description: 'Manually starred sighting',
              },
            }),
            meta: {
              parentPath: ref.path,
              speciesDocPath: selectedSpeciesDoc ? `${ref.path}/perSpecies/${selectedSpeciesDoc.id}` : '',
              parentDoc,
              speciesDoc,
            },
          };
        }),
      );

      if (!isMountedRef.current) {
        return;
      }

      const sortedHighlights = nextHighlights
        .filter(Boolean)
        .sort(compareHighlightsByHighlightedAt);
      const lastPageIndex = Math.max(
        0,
        Math.ceil(sortedHighlights.length / HIGHLIGHTS_PAGE_SIZE) - 1,
      );

      setAllHighlights(sortedHighlights);
      setCurrentPage((previousPage) => (
        shouldPreservePage ? Math.min(previousPage, lastPageIndex) : 0
      ));
    } catch (err) {
      console.error('Failed to fetch highlights', err);
      if (isMountedRef.current) {
        setError(getHighlightLoadErrorMessage(err, 'Unable to load highlights'));
        setAllHighlights([]);
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [accessReady, allowedLocationSet, isAdmin]);

  useEffect(() => {
    loadHighlights();
  }, [loadHighlights, refreshToken]);

  useEffect(() => {
    if (!activeEntry) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        trackButton('highlight_close', {
          source: 'keyboard',
          species: activeEntry?.species,
          category: activeEntry?.category,
        });
        setActiveEntry(null);
        setModalViewMode('standard');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [activeEntry]);

  const highlights = useMemo(() => {
    const startIndex = currentPage * HIGHLIGHTS_PAGE_SIZE;
    return allHighlights.slice(startIndex, startIndex + HIGHLIGHTS_PAGE_SIZE);
  }, [allHighlights, currentPage]);
  const availableSpecies = useMemo(() => (
    allHighlights
      .map((entry) => (typeof entry?.species === 'string' ? entry.species.trim() : ''))
      .filter((label) => typeof label === 'string' && label.trim().length > 0)
      .filter((label, index, all) => all.indexOf(label) === index)
      .sort((a, b) => a.localeCompare(b))
  ), [allHighlights]);
  const hasHighlights = highlights.length > 0;
  const hasPreviousPage = currentPage > 0;
  const hasMore = (currentPage + 1) * HIGHLIGHTS_PAGE_SIZE < allHighlights.length;

  const handleNextPage = useCallback(() => {
    if (loading || !hasMore) {
      return;
    }
    trackButton('highlight_page_next');
    setCurrentPage((prev) => prev + 1);
  }, [hasMore, loading]);

  const handlePreviousPage = useCallback(() => {
    if (loading || !hasPreviousPage) {
      return;
    }
    trackButton('highlight_page_previous');
    setCurrentPage((prev) => Math.max(0, prev - 1));
  }, [hasPreviousPage, loading]);

  const handleOpenEntry = (entry, source = 'card') => {
    setActiveEntry(entry);
    setModalViewMode('standard');
    trackButton('highlight_open', {
      source,
      species: entry?.species,
      category: entry?.category,
      mediaType: entry?.mediaType,
    });
  };

  const handleCloseModal = (source = 'dismiss') => {
    setActiveEntry(null);
    setModalViewMode('standard');
    trackButton('highlight_close', {
      source,
      species: activeEntry?.species,
      category: activeEntry?.category,
    });
  };

  const isDebugMode = modalViewMode === 'debug';

  const selectedFacebookPage = useMemo(
    () => facebookConnection.pages.find(
      (page) => page.id === facebookConnection.selectedPageId,
    ) || null,
    [facebookConnection.pages, facebookConnection.selectedPageId],
  );

  const handleOpenFacebookPublish = useCallback((entry, source = 'card') => {
    if (!entry || entry.mediaType !== 'video') return;
    if (facebookConnection.loading) {
      window.alert('TingVision is still checking your Facebook Page connection. Try again in a moment.');
      return;
    }
    if (!facebookConnection.connected || !selectedFacebookPage) {
      window.alert(
        facebookConnection.error
          || 'Connect and choose a Facebook Page in Settings before posting a video.',
      );
      return;
    }
    if (selectedFacebookPage.canPublish === false) {
      window.alert('Reconnect Facebook in Settings with permission to post to this Page.');
      return;
    }
    if (!entry.meta?.parentPath || !entry.meta?.speciesDocPath || !(entry.videoUrl || entry.mediaUrl)) {
      window.alert('This highlight is missing the video or sighting details needed to post to Facebook.');
      return;
    }

    trackButton('highlight_facebook_review', {
      source,
      species: entry.species,
      location: entry.locationId,
      pageId: selectedFacebookPage.id,
    });
    setFacebookPublishTarget(entry);
    if (source === 'modal') {
      setActiveEntry(null);
      setModalViewMode('standard');
    }
  }, [facebookConnection, selectedFacebookPage]);

  const handleFacebookPublished = useCallback((result) => {
    trackEvent('highlight_facebook_publish_success', {
      species: facebookPublishTarget?.species,
      location: facebookPublishTarget?.locationId,
      pageId: result?.pageId,
      duplicatePrevented: Boolean(result?.duplicatePrevented),
    });
  }, [facebookPublishTarget]);

  const handleToggleModalMode = () => {
    const nextMode = modalViewMode === 'debug' ? 'standard' : 'debug';
    setModalViewMode(nextMode);
    trackButton('highlight_toggle_view', {
      mode: nextMode,
      species: activeEntry?.species,
      category: activeEntry?.category,
    });
  };

  const handleDownloadHighlight = useCallback(
    async (entry, source = 'card') => {
      if (!entry) {
        return;
      }

      setDownloadStatusMap((prev) => ({
        ...prev,
        [entry.id]: { state: 'pending' },
      }));

      try {
        await downloadEntryMedia(entry);
        trackButton('highlight_download', {
          source,
          mediaType: entry.mediaType,
          species: entry.species,
          location: entry.locationId,
        });
      } catch (err) {
        console.error('Failed to download highlight media', err);
        if (typeof window !== 'undefined') {
          window.alert(err?.message || 'Unable to download this highlight.');
        }
      } finally {
        setDownloadStatusMap((prev) => ({
          ...prev,
          [entry.id]: { state: 'idle' },
        }));
      }
    },
    [],
  );

  const handleRemoveHighlight = useCallback(
    async (entry, source = 'card') => {
      if (!entry) {
        return;
      }

      const highlightKey = getHighlightStateKey(entry);
      if (!highlightKey) {
        return;
      }

      if (typeof window !== 'undefined') {
        const confirmed = window.confirm(
          'Remove this sighting from highlights? It will stay saved, but it will no longer appear on the home page.',
        );
        if (!confirmed) {
          return;
        }
      }

      trackButton('highlight_remove', {
        source,
        species: entry.species,
        location: entry.locationId,
      });

      setHighlightStatusMap((prev) => ({
        ...prev,
        [highlightKey]: {
          state: 'pending',
          message: 'Removing from highlights…',
        },
      }));

      try {
        await applySightingHighlight({
          entry,
          enabled: false,
          actor: actorName,
        });

        setAllHighlights((prev) => prev.filter((candidate) => getHighlightStateKey(candidate) !== highlightKey));
        setActiveEntry((current) => (getHighlightStateKey(current) === highlightKey ? null : current));
        setHighlightStatusMap((prev) => {
          const next = { ...prev };
          delete next[highlightKey];
          return next;
        });
        trackEvent('highlight_remove_success', {
          source,
          species: entry.species,
          location: entry.locationId,
        });
        refreshHighlights();
      } catch (err) {
        console.error('Failed to remove highlight', err);
        trackEvent('highlight_remove_error', {
          source,
          species: entry.species,
          location: entry.locationId,
          error: toAnalyticsError(err?.message || 'Unable to remove highlight.'),
        });
        setHighlightStatusMap((prev) => ({
          ...prev,
          [highlightKey]: {
            state: 'error',
            message: err?.message || 'Unable to remove highlight.',
          },
        }));
      }
    },
    [actorName, refreshHighlights],
  );

  const renderModalMedia = () => {
    if (!activeEntry) {
      return <div className="highlightCard__placeholder">No preview available</div>;
    }

    const isVideo = activeEntry.mediaType === 'video';
    const debugMedia = activeEntry.debugUrl || null;
    const hasDebugMedia = Boolean(debugMedia);
    const debugIsVideo = isLikelyVideoUrl(debugMedia);
    const useDebugMedia = isDebugMode && hasDebugMedia;

    if (isVideo) {
      if (useDebugMedia) {
        if (debugIsVideo) {
          return (
            <video
              key={`debug-${debugMedia}`}
              src={debugMedia}
              controls
              autoPlay
              playsInline
            />
          );
        }
        return (
          <img
            key={`debugimg-${debugMedia}`}
            src={debugMedia}
            alt={`${activeEntry.species} highlight debug`}
          />
        );
      }

      const primaryVideo = activeEntry.videoUrl || null;
      const hdVideo = activeEntry.mediaType === 'video' ? activeEntry.mediaUrl || null : null;
      const selectedVideo = primaryVideo || hdVideo || null;

      if (selectedVideo) {
        return (
          <video
            key={`primary-${selectedVideo}`}
            src={selectedVideo}
            controls
            autoPlay
            playsInline
          />
        );
      }

      if (hasDebugMedia) {
        if (debugIsVideo) {
          return (
            <video
              key={`fallback-debug-${debugMedia}`}
              src={debugMedia}
              controls
              autoPlay
              playsInline
            />
          );
        }
        return (
          <img
            key={`fallback-debugimg-${debugMedia}`}
            src={debugMedia}
            alt={`${activeEntry.species} highlight debug`}
          />
        );
      }

      if (activeEntry.previewUrl) {
        return (
          <img
            key={`preview-${activeEntry.previewUrl}`}
            src={activeEntry.previewUrl}
            alt={`${activeEntry.species} highlight enlarged`}
          />
        );
      }

      return <div className="highlightCard__placeholder">No preview available</div>;
    }

    const standardImage = activeEntry.previewUrl || null;
    const hdImage = activeEntry.mediaUrl || null;

    if (useDebugMedia) {
      if (debugIsVideo) {
        return (
          <video
            key={`debug-${debugMedia}`}
            src={debugMedia}
            controls
            autoPlay
            playsInline
          />
        );
      }
      return (
        <img
          key={`debugimg-${debugMedia}`}
          src={debugMedia}
          alt={`${activeEntry.species} highlight debug`}
        />
      );
    }

    const hasHdImageAlternative = Boolean(hdImage && hdImage !== standardImage);
    const shouldUseHdImage = isHdEnabled && hasHdImageAlternative;
    const displayImage = shouldUseHdImage ? hdImage : (standardImage || hdImage || null);

    if (displayImage) {
      const isHdImage = displayImage === hdImage && hdImage;
      const hdLabel = isHdImage ? ' HD' : '';
      return (
        <img
          key={`image-${modalViewMode}-${displayImage}`}
          src={displayImage}
          alt={`${activeEntry.species} highlight${hdLabel} enlarged`}
        />
      );
    }

    if (hasDebugMedia) {
      if (debugIsVideo) {
        return (
          <video
            key={`fallback-debug-${debugMedia}`}
            src={debugMedia}
            controls
            autoPlay
            playsInline
          />
        );
      }
      return (
        <img
          key={`fallback-debugimg-${debugMedia}`}
          src={debugMedia}
          alt={`${activeEntry.species} highlight debug`}
        />
      );
    }

    return <div className="highlightCard__placeholder">No preview available</div>;
  };

  const hasHdImageAlternative = Boolean(
    activeEntry
    && activeEntry.mediaType !== 'video'
    && activeEntry.mediaUrl
    && activeEntry.mediaUrl !== activeEntry.previewUrl,
  );

  const hasDebugMedia = Boolean(activeEntry?.debugUrl);

  const hasModalMedia = Boolean(
    activeEntry
    && (activeEntry.videoUrl
      || activeEntry.mediaUrl
      || activeEntry.previewUrl
      || activeEntry.debugUrl),
  );

  const editChange = useMemo(() => {
    if (!editTarget) {
      return null;
    }
    return describeSpeciesChange({ mode: editMode, species: editSpeciesInput });
  }, [editTarget, editMode, editSpeciesInput]);

  const editNotePreview = useMemo(() => {
    if (!editTarget || !editChange) {
      return '';
    }
    return buildCorrectionNote({
      actor: actorName,
      previousSpecies: editTarget.species,
      nextLabel: editChange.label,
      locationId: editTarget.locationId,
      includeTimestamp: false,
    });
  }, [editTarget, editChange, actorName]);

  const handleOpenEditModal = useCallback((entry, source = 'card') => {
    if (!entry) {
      return;
    }

    const normalizedSpecies = typeof entry.species === 'string' ? entry.species.trim() : '';
    const initialMode = normalizedSpecies.toLowerCase() === 'background' ? 'background' : 'animal';

    setEditTarget(entry);
    setEditMode(initialMode);
    setEditSpeciesInput(initialMode === 'animal' ? normalizedSpecies : '');
    setEditError('');
    trackButton('highlight_edit_open', {
      source,
      species: entry.species,
      location: entry.locationId,
    });
  }, []);

  const handleCloseEditModal = useCallback((source = 'dismiss') => {
    if (editSaving) {
      return;
    }
    if (editTarget) {
      trackButton('highlight_edit_close', {
        source,
        species: editTarget.species,
        location: editTarget.locationId,
      });
    }
    setEditTarget(null);
    setEditMode('animal');
    setEditSpeciesInput('');
    setEditError('');
  }, [editSaving, editTarget]);

  const handleEditModeChange = useCallback((event) => {
    const nextMode = event.target.value;
    setEditMode(nextMode);
    if (nextMode === 'background') {
      setEditSpeciesInput('');
    }
    trackEvent('highlight_edit_mode', { mode: nextMode });
  }, []);

  const handleSubmitEdit = useCallback(
    async (event) => {
      event.preventDefault();
      if (!editTarget) {
        return;
      }

      if (editMode === 'animal') {
        const trimmed = editSpeciesInput.trim();
        if (!trimmed) {
          setEditError('Please enter a species name.');
          return;
        }
      }

      setEditSaving(true);
      setEditError('');
      trackButton('highlight_edit_save', {
        mode: editMode,
        species: editTarget.species,
        location: editTarget.locationId,
      });

      try {
        const change = describeSpeciesChange({ mode: editMode, species: editSpeciesInput });
        const finalNote = buildCorrectionNote({
          actor: actorName,
          previousSpecies: editTarget.species,
          nextLabel: change.label,
          locationId: editTarget.locationId,
        });

        const authToken =
          typeof user?.getIdToken === 'function' ? await user.getIdToken() : null;

        await applySightingCorrection({
          entry: editTarget,
          mode: editMode,
          nextSpeciesName: editSpeciesInput,
          actor: actorName,
          note: finalNote,
          change,
          relocateMedia: true,
          authToken,
        });

        trackEvent('highlight_edit_success', {
          mode: editMode,
          previousSpecies: editTarget.species,
          nextSpecies: change.label,
          location: editTarget.locationId,
        });
        handleCloseEditModal('save');
        refreshHighlights({ preservePage: true });
      } catch (err) {
        console.error('Failed to correct highlight sighting', err);
        trackEvent('highlight_edit_error', {
          mode: editMode,
          location: editTarget.locationId,
          error: toAnalyticsError(err?.message || 'Unable to update sighting.'),
        });
        setEditError(err?.message || 'Unable to update sighting.');
      } finally {
        setEditSaving(false);
      }
    },
    [editTarget, editMode, editSpeciesInput, actorName, user, handleCloseEditModal, refreshHighlights],
  );

  const handleSendToWhatsApp = useCallback(
    async (entry, options = {}) => {
      const { alertStyle } = options;
      const isAlert = alertStyle === 'emoji';

      if (!entry || typeof entry.id !== 'string') {
        return;
      }

      const entryId = entry.id;
      if (sendingEntryIdsRef.current.has(entryId)) {
        return;
      }

      sendingEntryIdsRef.current.add(entryId);
      const updateSendStatus = (status) => {
        setSendStatusMap((prev) => ({
          ...prev,
          [entryId]: status,
        }));
      };

      updateSendStatus({ state: 'pending' });

      let entryForSend = entry;
      let speciesToSend = entry?.species;
      let correctionResult = null;
      let requestedCorrectionSpecies = '';
      let videoClipSelection = DEFAULT_WHATSAPP_CLIP_SELECTION;

      try {
        if (!SEND_WHATSAPP_ENDPOINT) {
          updateSendStatus({
            state: 'error',
            message: 'WhatsApp sending is not configured.',
          });
          return;
        }

        if (!entry.locationId) {
          updateSendStatus({
            state: 'error',
            message: 'Location information is missing for this sighting.',
          });
          return;
        }

        if (typeof window !== 'undefined') {
          const wantsCorrection = window.confirm('Do you want to change the animal before sending?');
          if (wantsCorrection) {
            const manualSpecies = window.prompt(
              'Video/photo of the',
              speciesToSend || '',
            );

            if (typeof manualSpecies === 'string') {
              const normalizedSpecies = manualSpecies.trim();
              requestedCorrectionSpecies = normalizedSpecies;
              speciesToSend = normalizedSpecies || speciesToSend;
            }
          }

          if (isAlert) {
            const spellingReview = reviewAlertSpeciesSpelling(speciesToSend, window);
            if (spellingReview.cancelled) {
              updateSendStatus({ state: 'idle', message: '' });
              return;
            }

            if (spellingReview.species !== speciesToSend) {
              requestedCorrectionSpecies = spellingReview.species;
              speciesToSend = spellingReview.species;
            }
          }

          const mediaSource = pickFirstSource(entryForSend.mediaUrl, entryForSend.videoUrl, entryForSend.previewUrl);
          if (!mediaSource) {
            updateSendStatus({
              state: 'error',
              message: 'No media is available to send for this sighting.',
            });
            return;
          }

          if (entryForSend.mediaType === 'video') {
            const clipChoice = promptForWhatsAppClipSelection();
            if (clipChoice.cancelled) {
              updateSendStatus({ state: 'idle', message: '' });
              return;
            }
            videoClipSelection = clipChoice.clipSelection;
          }
        }

        let mediaSource = pickFirstSource(entryForSend.mediaUrl, entryForSend.videoUrl, entryForSend.previewUrl);
        if (!mediaSource) {
          updateSendStatus({
            state: 'error',
            message: 'No media is available to send for this sighting.',
          });
          return;
        }

        const confirmationMessage =
          options.confirmationMessage ||
          (isAlert
            ? 'Send this sighting as an alert to WhatsApp groups?'
            : 'Send this sighting to WhatsApp?');

        const confirmationWithClip = entryForSend.mediaType === 'video'
          ? `${confirmationMessage}\n\nVideo clip: ${formatWhatsAppClipSelection(videoClipSelection)}`
          : confirmationMessage;

        if (typeof window !== 'undefined' && !window.confirm(confirmationWithClip)) {
          updateSendStatus({ state: 'idle', message: '' });
          return;
        }

        if (isAlert && requestedCorrectionSpecies) {
          const change = describeSpeciesChange({ mode: 'animal', species: speciesToSend });
          const finalNote = buildCorrectionNote({
            actor: actorName,
            previousSpecies: entry.species,
            nextLabel: change.label,
            locationId: entry.locationId,
          });
          const authToken =
            typeof user?.getIdToken === 'function' ? await user.getIdToken() : null;

          correctionResult = await applySightingCorrection({
            entry,
            mode: 'animal',
            nextSpeciesName: speciesToSend,
            actor: actorName,
            note: finalNote,
            change,
            relocateMedia: true,
            authToken,
          });

          entryForSend = {
            ...entry,
            ...correctionResult.parentDocUpdates,
            species: correctionResult.change.label,
            mediaUrl: correctionResult.parentDocUpdates.mediaUrl ?? entry.mediaUrl,
            previewUrl: correctionResult.parentDocUpdates.previewUrl ?? entry.previewUrl,
            videoUrl: correctionResult.parentDocUpdates.videoUrl ?? entry.videoUrl,
            debugUrl: correctionResult.parentDocUpdates.debugUrl ?? entry.debugUrl,
            meta: entry.meta
              ? {
                ...entry.meta,
                parentDoc: {
                  ...(entry.meta.parentDoc || {}),
                  ...correctionResult.parentDocUpdates,
                },
                speciesDoc: {
                  ...(entry.meta.speciesDoc || {}),
                  ...(correctionResult.speciesDocUpdates || {}),
                  id: correctionResult.speciesDocId || entry.meta?.speciesDoc?.id,
                },
                speciesDocPath: correctionResult.speciesDocPath || entry.meta?.speciesDocPath,
              }
              : entry.meta,
          };
          speciesToSend = correctionResult.change.normalizedName;
          mediaSource = pickFirstSource(entryForSend.mediaUrl, entryForSend.videoUrl, entryForSend.previewUrl);
          if (!mediaSource) {
            updateSendStatus({
              state: 'error',
              message: 'No media is available to send for this sighting.',
            });
            return;
          }
        }

        trackButton(isAlert ? 'highlight_send_whatsapp_alert' : 'highlight_send_whatsapp');

        const payload = {
          locationId: entryForSend.locationId,
          gcp_url: mediaSource,
          media_url: mediaSource,
          timestamp:
            entryForSend.createdAt instanceof Date && !Number.isNaN(entryForSend.createdAt.getTime())
              ? entryForSend.createdAt.toISOString()
              : undefined,
          mediaType: entryForSend.mediaType,
          displaySpecies: requestedCorrectionSpecies || speciesToSend,
          species: speciesToSend,
          primarySpecies: speciesToSend,
          alertStyle,
          manualAlert: isAlert,
          clipSelection: videoClipSelection,
          clip_selection: videoClipSelection,
          parentPath: entryForSend?.meta?.parentPath || '',
          speciesDocPath: entryForSend?.meta?.speciesDocPath || '',
        };

        const response = await fetch(SEND_WHATSAPP_ENDPOINT, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });

        const contentType = response.headers.get('content-type') || '';
        const responseBody = contentType.includes('application/json')
          ? await response.json().catch(() => ({}))
          : await response.text();

        if (!response.ok) {
          const errorMessage =
            typeof responseBody === 'string'
              ? responseBody
              : responseBody?.error || 'Failed to send WhatsApp alert';
          throw new Error(errorMessage);
        }

        const cooldownWarning = Boolean(
          isAlert
          && responseBody?.cooldown
          && responseBody.cooldown.recorded === false
          && responseBody.cooldown.reason !== 'custom_species',
        );

        trackEvent(isAlert ? 'highlight_send_whatsapp_alert_success' : 'highlight_send_whatsapp_success', {
          location: entryForSend.locationId,
          mediaType: entryForSend.mediaType,
          alertStyle: alertStyle || 'legacy',
          correctedBeforeSend: Boolean(correctionResult),
          clipSelection: entryForSend.mediaType === 'video' ? videoClipSelection : null,
          cooldownReset: responseBody?.cooldown?.recorded ?? null,
        });

        updateSendStatus({
          state: cooldownWarning ? 'error' : 'success',
          message: cooldownWarning
            ? 'Alert sent, but cooldown was not reset.'
            : (isAlert ? 'Alert sent to WhatsApp' : 'Sent to WhatsApp'),
        });

        if (correctionResult) {
          refreshHighlights();
        }
      } catch (err) {
        console.error('Failed to send WhatsApp alert', err);
        const message = err instanceof Error && err.message ? err.message : 'Failed to send to WhatsApp';

        trackEvent(isAlert ? 'highlight_send_whatsapp_alert_error' : 'highlight_send_whatsapp_error', {
          location: entryForSend.locationId,
          mediaType: entryForSend.mediaType,
          error: message,
          alertStyle: alertStyle || 'legacy',
          clipSelection: entryForSend.mediaType === 'video' ? videoClipSelection : null,
        });

        updateSendStatus({
          state: 'error',
          message,
        });
      } finally {
        sendingEntryIdsRef.current.delete(entryId);
      }
    },
    [actorName, refreshHighlights, user],
  );

  const handleDeleteHighlights = useCallback(
    async (entries, options = {}) => {
      const { source = 'card' } = options;
      const targets = entries.filter((entry) => entry && entry.id);
      if (targets.length === 0) {
        return;
      }

      if (typeof window !== 'undefined') {
        const confirmationMessage = targets.length === 1
          ? 'Delete this sighting? This will remove its media files and hide it from the feed.'
          : `Delete ${targets.length} sightings? This will remove their media files and hide them from the feed.`;
        const confirmed = window.confirm(confirmationMessage);
        if (!confirmed) {
          return;
        }
      }

      trackButton('highlight_delete', {
        source,
        count: targets.length,
      });

      setDeleteStatusMap((prev) => {
        const next = { ...prev };
        targets.forEach((entry) => {
          next[entry.id] = { state: 'pending', message: 'Deleting…' };
        });
        return next;
      });

      let authToken = null;
      try {
        authToken = typeof user?.getIdToken === 'function' ? await user.getIdToken() : null;
      } catch (err) {
        authToken = null;
      }

      try {
        const results = await Promise.all(
          targets.map(async (entry) => {
            try {
              if (!DELETE_SIGHTING_ENDPOINT) {
                throw new Error('Delete endpoint is not configured.');
              }

              const parentPath = entry?.meta?.parentPath;
              if (!parentPath) {
                throw new Error('Sighting metadata is missing required references.');
              }

              if (!authToken) {
                throw new Error('Missing auth token for delete.');
              }

              const response = await fetch(DELETE_SIGHTING_ENDPOINT, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${authToken}`,
                },
                body: JSON.stringify({
                  parentPath,
                  actorName,
                }),
              });

              const contentType = response.headers.get('content-type') || '';
              const responseBody = contentType.includes('application/json')
                ? await response.json().catch(() => ({}))
                : await response.text();

              if (!response.ok) {
                const message =
                  typeof responseBody === 'string'
                    ? responseBody
                    : responseBody?.error || 'Unable to delete sighting.';
                throw new Error(message);
              }

              return { id: entry.id, status: 'success', message: 'Sighting deleted.' };
            } catch (err) {
              console.error('Failed to delete sighting', err);
              return {
                id: entry.id,
                status: 'error',
                message: err?.message || 'Unable to delete sighting.',
              };
            }
          }),
        );

        setDeleteStatusMap((prev) => {
          const next = { ...prev };
          results.forEach(({ id, status, message }) => {
            next[id] = { state: status, message };
          });
          return next;
        });

        const successCount = results.filter(({ status }) => status === 'success').length;
        const errorCount = results.length - successCount;
        trackEvent('highlight_delete_result', {
          source,
          successCount,
          errorCount,
        });

        if (results.some(({ status }) => status === 'success')) {
          refreshHighlights();
        }
      } catch (err) {
        console.error('Failed to delete highlights', err);
        trackEvent('highlight_delete_error', {
          source,
          count: targets.length,
          error: toAnalyticsError(err?.message || 'Failed to delete highlights'),
        });
      }
    },
    [actorName, refreshHighlights, user],
  );

  return (
    <section className="highlights">
      <header className="highlights__header">
        <div>
          <h2>Recent Highlights</h2>
          <p>Showing 50 starred sightings per page, ordered by when they were highlighted.</p>
        </div>
        {loading && <span className="highlights__status">Loading…</span>}
        {!loading && error && <span className="highlights__status highlights__status--error">{error}</span>}
        {accessReady && accessError && (
          <span className="highlights__status highlights__status--error">{accessError}</span>
        )}
      </header>

      {noAssignedLocations && (
        <div className="highlights__empty">No locations have been assigned to your account yet.</div>
      )}

      {!loading && !error && !hasHighlights && !noAssignedLocations && (
        <div className="highlights__empty">No highlights have been starred yet.</div>
      )}

      {hasHighlights && (
        <div className="highlights__grid">
          {highlights.map((entry) => {
            const sendStatus = sendStatusMap[entry.id] || { state: 'idle', message: '' };
            const isSending = sendStatus.state === 'pending';
            const deleteStatus = deleteStatusMap[entry.id] || { state: 'idle', message: '' };
            const isDeleting = deleteStatus.state === 'pending';
            const downloadStatus = downloadStatusMap[entry.id] || { state: 'idle' };
            const isDownloading = downloadStatus.state === 'pending';
            const highlightStatusKey = getHighlightStateKey(entry);
            const highlightStatus = highlightStatusMap[highlightStatusKey] || { state: 'idle', message: '' };
            const isHighlighting = highlightStatus.state === 'pending';

            return (
              <article className="highlightCard" key={entry.parentId || entry.id}>
                <div className="highlightCard__media">
                  <button
                    type="button"
                    className="highlightCard__mediaButton"
                    onClick={() => handleOpenEntry(entry)}
                    disabled={isSending}
                    aria-label={`Open highlight preview for ${entry.species}`}
                  >
                    {(() => {
                      const fallbackImage = entry.mediaType !== 'video' ? entry.mediaUrl : null;
                      const debugImage = !isLikelyVideoUrl(entry.debugUrl)
                        ? entry.debugUrl
                        : null;
                      const previewSrc = entry.previewUrl || fallbackImage || debugImage;
                      if (previewSrc) {
                        return <img src={previewSrc} alt={`${entry.species} highlight`} />;
                      }
                      if (entry.mediaType === 'video' && (entry.videoUrl || entry.mediaUrl)) {
                        return <video src={`${entry.videoUrl || entry.mediaUrl}#t=0.1`} muted playsInline preload="metadata" aria-label={`${entry.species} highlight preview`} />;
                      }
                      return <div className="highlightCard__placeholder">No preview available</div>;
                    })()}
                    <span className="highlightCard__badge">
                      {entry.mediaType === 'video' ? 'Video' : 'Image'}
                    </span>
                  </button>
                </div>
                <div className="highlightCard__body">
                  <div className="highlightCard__headline">
                    <span className="highlightCard__label">Starred Highlight</span>
                    <h4 className="highlightCard__title">{formatCountWithSpecies(entry.species, entry.count)}</h4>
                  </div>
                  <div className="highlightCard__meta">
                    {entry.highlightedAt && (
                      <span>Highlighted: {formatDateTimeLabel(entry.highlightedAt)}</span>
                    )}
                    {typeof entry.maxConf === 'number' && (
                      <span>Confidence: {formatPercent(entry.maxConf)}</span>
                    )}
                  </div>
                  <div className="highlightCard__footer">
                    <div className="highlightCard__footerGroup">
                      <span className="highlightCard__footerLabel">Location</span>
                      <span className="highlightCard__location" title={entry.locationId}>{entry.locationId}</span>
                    </div>
                    {entry.spottedAt && (
                      <div className="highlightCard__footerGroup">
                        <span className="highlightCard__footerLabel">Seen</span>
                        <time className="highlightCard__time" dateTime={entry.spottedAt.toISOString()}>
                          {formatDateTimeLabel(entry.spottedAt)}
                        </time>
                      </div>
                    )}
                  </div>
                  <div className="highlightCard__actions">
                    <div className="highlightCard__actionsRow">
                      {isAdmin && (
                        <button
                          type="button"
                          className="highlightCard__editButton"
                          onClick={() => handleOpenEditModal(entry, 'card')}
                          disabled={editSaving || isDeleting || isHighlighting || isSending}
                          aria-label={`Edit sighting for ${entry.species}`}
                          title="Edit sighting"
                        >
                          <FiEdit2 />
                        </button>
                      )}
                      {isAdmin && (
                        <button
                          type="button"
                          className="highlightCard__actionsButton highlightCard__actionsButton--highlight"
                          onClick={() => handleRemoveHighlight(entry, 'card')}
                          disabled={isHighlighting || isDeleting || isSending || isDownloading}
                        >
                          <FiStar />
                          {isHighlighting ? 'Removing…' : 'Remove highlight'}
                        </button>
                      )}
                      <button
                        type="button"
                        className="highlightCard__actionsButton highlightCard__actionsButton--download"
                        onClick={() => handleDownloadHighlight(entry)}
                        disabled={isDeleting || isDownloading || isHighlighting || isSending}
                      >
                        {isDownloading ? <span className="highlightCard__buttonSpinner" /> : <FiDownload />}
                        {isDownloading ? 'Preparing…' : 'Download'}
                      </button>
                      <button
                        type="button"
                        className="highlightCard__actionsButton"
                        onClick={() => handleSendToWhatsApp(entry)}
                        disabled={isSending || isDeleting || isHighlighting}
                      >
                        {isSending ? 'Sending…' : 'Send to WhatsApp'}
                      </button>
                      {entry.mediaType === 'video' && (
                        <button
                          type="button"
                          className="highlightCard__actionsButton highlightCard__actionsButton--facebook"
                          onClick={() => handleOpenFacebookPublish(entry)}
                          disabled={isDeleting || isHighlighting || isSending}
                          title={selectedFacebookPage
                            ? `Review post to ${selectedFacebookPage.name}`
                            : 'Connect a Facebook Page in Settings'}
                        >
                          <FaFacebookF />
                          Post to Facebook
                        </button>
                      )}
                      <button
                        type="button"
                        className="highlightCard__actionsButton highlightCard__actionsButton--alert"
                        onClick={() =>
                          handleSendToWhatsApp(entry, {
                            alertStyle: 'emoji',
                            confirmationMessage: 'Send this sighting as an alert to WhatsApp groups?',
                          })
                        }
                        disabled={isSending || isDeleting || isHighlighting}
                      >
                        {isSending ? 'Sending…' : 'Alert'}
                      </button>
                      {isAdmin && (
                        <button
                          type="button"
                          className="highlightCard__actionsButton highlightCard__actionsButton--danger"
                          onClick={() => handleDeleteHighlights([entry], { source: 'card' })}
                          disabled={isDeleting || isSending || isHighlighting}
                        >
                          {isDeleting ? 'Deleting…' : 'Delete'}
                        </button>
                      )}
                    </div>
                    {sendStatus.state === 'success' && sendStatus.message && (
                      <span className="highlightCard__actionsMessage highlightCard__actionsMessage--success">
                        {sendStatus.message}
                      </span>
                    )}
                    {sendStatus.state === 'error' && sendStatus.message && (
                      <span className="highlightCard__actionsMessage highlightCard__actionsMessage--error">
                        {sendStatus.message}
                      </span>
                    )}
                    {isAdmin && highlightStatus.state === 'error' && highlightStatus.message && (
                      <span className="highlightCard__actionsMessage highlightCard__actionsMessage--error">
                        {highlightStatus.message}
                      </span>
                    )}
                    {isAdmin && deleteStatus.state === 'success' && deleteStatus.message && (
                      <span className="highlightCard__actionsMessage highlightCard__actionsMessage--success">
                        {deleteStatus.message}
                      </span>
                    )}
                    {isAdmin && deleteStatus.state === 'error' && deleteStatus.message && (
                      <span className="highlightCard__actionsMessage highlightCard__actionsMessage--error">
                        {deleteStatus.message}
                      </span>
                    )}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}

      {(hasHighlights || hasPreviousPage) && (
        <div className="highlights__pagination" role="navigation" aria-label="Highlights pagination">
          <button
            type="button"
            className="highlights__pageButton"
            onClick={handlePreviousPage}
            disabled={!hasPreviousPage || loading}
          >
            Previous
          </button>
          <span className="highlights__pageStatus">
            Page {currentPage + 1}
          </span>
          <button
            type="button"
            className="highlights__pageButton"
            onClick={handleNextPage}
            disabled={!hasMore || loading}
          >
            Next
          </button>
        </div>
      )}

      {activeEntry && (
        <div
          className="highlightModal"
          role="dialog"
          aria-modal="true"
          onClick={(event) => {
            if (event.target === event.currentTarget) {
              handleCloseModal('overlay');
            }
          }}
        >
          <div className="highlightModal__content">
            <button
              type="button"
              className="highlightModal__close"
              onClick={() => handleCloseModal('button')}
              aria-label="Close highlight preview"
            >
              ×
            </button>
            {hasModalMedia && (
              <div className="highlightModal__controls">
                {hasHdImageAlternative && (
                  <button
                    type="button"
                    className={`highlightModal__toggle${isHdEnabled ? ' is-active' : ''}`}
                    onClick={() => {
                      const nextValue = !isHdEnabled;
                      setIsHdEnabled(nextValue);
                      trackButton('highlight_toggle_hd', {
                        enabled: nextValue,
                        species: activeEntry?.species,
                        category: activeEntry?.category,
                      });
                    }}
                  >
                    {isHdEnabled ? 'Standard Quality' : 'View in HD'}
                  </button>
                )}
                {hasDebugMedia && (
                  <button
                    type="button"
                    className={`highlightModal__toggle${isDebugMode ? ' is-active' : ''}`}
                    onClick={handleToggleModalMode}
                  >
                    {isDebugMode ? 'Standard View' : 'Debug'}
                  </button>
                )}
              </div>
            )}
            <div className="highlightModal__media">{renderModalMedia()}</div>
            <div className="highlightModal__details">
              <h4>{activeEntry.species}</h4>
              <p>{activeEntry.label}</p>
              <div className="highlightModal__meta">
                {typeof activeEntry.count === 'number' && (
                  <span>Count: {activeEntry.count}</span>
                )}
                {typeof activeEntry.maxConf === 'number' && (
                  <span>Confidence: {formatPercent(activeEntry.maxConf)}</span>
                )}
                {activeEntry.highlightedAt && (
                  <span>Highlighted: {formatDateTimeLabel(activeEntry.highlightedAt)}</span>
                )}
                {activeEntry.spottedAt && (
                  <time dateTime={activeEntry.spottedAt.toISOString()}>
                    Seen: {formatDateTimeLabel(activeEntry.spottedAt)}
                  </time>
                )}
              </div>
              <div className="highlightModal__actions">
                <button
                  type="button"
                  className="highlightCard__actionsButton highlightCard__actionsButton--download"
                  onClick={() => handleDownloadHighlight(activeEntry, 'modal')}
                  disabled={
                    (downloadStatusMap[activeEntry.id] || { state: 'idle' }).state === 'pending' ||
                    (sendStatusMap[activeEntry.id] || { state: 'idle' }).state === 'pending'
                  }
                >
                  {(downloadStatusMap[activeEntry.id] || { state: 'idle' }).state === 'pending'
                    ? <span className="highlightCard__buttonSpinner" />
                    : <FiDownload />}
                  {(downloadStatusMap[activeEntry.id] || { state: 'idle' }).state === 'pending'
                    ? 'Preparing…'
                    : 'Download'}
                </button>
                {activeEntry.mediaType === 'video' && (
                  <button
                    type="button"
                    className="highlightCard__actionsButton highlightCard__actionsButton--facebook"
                    onClick={() => handleOpenFacebookPublish(activeEntry, 'modal')}
                    disabled={
                      deleteStatusMap[activeEntry.id]?.state === 'pending' ||
                      highlightStatusMap[getHighlightStateKey(activeEntry)]?.state === 'pending' ||
                      sendStatusMap[activeEntry.id]?.state === 'pending'
                    }
                  >
                    <FaFacebookF />
                    Post to Facebook
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {facebookPublishTarget && selectedFacebookPage && (
        <FacebookVideoPublishModal
          key={facebookPublishTarget.id}
          entry={facebookPublishTarget}
          locationLabel={facebookPublishTarget.locationId}
          page={selectedFacebookPage}
          onClose={() => setFacebookPublishTarget(null)}
          onPublished={handleFacebookPublished}
        />
      )}

      {editTarget && (
        <div
          className="highlightEditModal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="highlightEditModalTitle"
          onClick={() => handleCloseEditModal('overlay')}
        >
          <div
            className="highlightEditModal__content"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="highlightEditModal__close"
              onClick={() => handleCloseEditModal('button')}
              disabled={editSaving}
            >
              Close
            </button>
            <h2 id="highlightEditModalTitle" className="highlightEditModal__title">Edit sighting</h2>
            <form onSubmit={handleSubmitEdit} className="highlightEditModal__form">
              <div className="highlightEditModal__section">
                <span className="highlightEditModal__label">Current classification</span>
                <p className="highlightEditModal__current">{editTarget.species || 'Unknown'}</p>
              </div>
              <div className="highlightEditModal__section">
                <span className="highlightEditModal__label">Update classification</span>
                <div className="highlightEditModal__radioGroup">
                  <label>
                    <input
                      type="radio"
                      name="highlightEditMode"
                      value="animal"
                      checked={editMode === 'animal'}
                      onChange={handleEditModeChange}
                      disabled={editSaving}
                    />
                    <span>Animal</span>
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="highlightEditMode"
                      value="background"
                      checked={editMode === 'background'}
                      onChange={handleEditModeChange}
                      disabled={editSaving}
                    />
                    <span>Background</span>
                  </label>
                </div>
              </div>
              {editMode === 'animal' && (
                <div className="highlightEditModal__section">
                  <label className="highlightEditModal__label" htmlFor="highlightEditSpecies">
                    Species
                  </label>
                  <input
                    id="highlightEditSpecies"
                    type="text"
                    value={editSpeciesInput}
                    onChange={(event) => setEditSpeciesInput(event.target.value)}
                    ref={editSpeciesInputRef}
                    className="highlightEditModal__input"
                    placeholder="Enter species name"
                    list="highlightEditSpeciesOptions"
                    disabled={editSaving}
                    required
                  />
                  <datalist id="highlightEditSpeciesOptions">
                    {availableSpecies.map((label) => (
                      <option key={label} value={label} />
                    ))}
                  </datalist>
                </div>
              )}
              <div className="highlightEditModal__section">
                <span className="highlightEditModal__label">Notes</span>
                <p className="highlightEditModal__note">
                  {editNotePreview || 'A note describing this correction will be recorded.'}
                </p>
              </div>
              {editError && <p className="highlightEditModal__error">{editError}</p>}
              <div className="highlightEditModal__actions">
                <button
                  type="button"
                  className="highlightEditModal__secondary"
                  onClick={() => handleCloseEditModal('cancel')}
                  disabled={editSaving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="highlightEditModal__primary"
                  disabled={editSaving}
                >
                  {editSaving ? 'Saving…' : 'Save changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
}
